import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart, formatINR } from "@/lib/cart";
import { ArrowLeft, Loader2, ShieldCheck, Building2, User, Lock, Eye, EyeOff } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { fileTypeIcon, fileTypeLabelKey } from "@/lib/fileTypes";

const GST_PERCENT = 18;

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

export default function Checkout() {
  const { t } = useI18n();
  const { user, refresh: refreshAuth } = useAuth();
  const { items, subtotalPaise, count, clear } = useCart();
  const nav = useNavigate();

  const [form, setForm] = useState({
    billing_name: user?.name || "",
    billing_mobile: user?.mobile || "",
    billing_email: user?.email || "",
    billing_address: "",
    billing_city: "",
    billing_state: "",
    billing_pincode: "",
    is_business: false,
    business_name: "",
    gstin: "",
    create_account: !user,    // checked by default for guests
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState("");

  const gst = useMemo(() => Math.floor((subtotalPaise * GST_PERCENT) / 100), [subtotalPaise]);
  const total = subtotalPaise + gst;

  useEffect(() => {
    // Don't bounce to /cart while a checkout is in flight or right after a
    // successful payment (clear() empties the cart synchronously and would
    // otherwise race the nav("/order/success")).
    if (paid || submitting) return;
    if (count === 0) nav("/cart");
  }, [count, submitting, paid, nav]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.billing_name.trim()) return t("checkout.errName");
    if (!/^\d{10}$/.test(form.billing_mobile.replace(/\D/g, ""))) return t("checkout.errMobile");
    if (!form.billing_address.trim()) return t("checkout.errAddress");
    if (!form.billing_city.trim()) return t("checkout.errCity");
    if (!form.billing_state) return t("checkout.errState");
    if (!/^\d{6}$/.test(form.billing_pincode.replace(/\D/g, ""))) return t("checkout.errPincode");
    if (form.is_business) {
      if (!form.business_name.trim()) return t("checkout.errBusinessName");
      if (!GSTIN_REGEX.test(form.gstin.toUpperCase())) return t("checkout.errGstin");
    }
    if (!user && form.create_account) {
      if (!form.password || form.password.length < 6) {
        return "Please choose a password (at least 6 characters) for your account.";
      }
    }
    return null;
  };

  const launchRazorpay = (orderResp) => {
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error(t("checkout.errSdk")));
        return;
      }
      const opts = {
        key: orderResp.key,
        amount: orderResp.amount,
        currency: orderResp.currency,
        name: "NakshaCart",
        description: `${orderResp.items.length} PDF${orderResp.items.length > 1 ? "s" : ""}`,
        order_id: orderResp.razorpay_order_id,
        prefill: {
          name: orderResp.name,
          email: orderResp.email,
          contact: orderResp.contact,
        },
        notes: { internal_order_id: orderResp.order_id },
        theme: { color: "#10B981" },
        handler: function (response) { resolve(response); },
        modal: { ondismiss: function () { reject(new Error(t("checkout.errCancelled"))); } },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", function (resp) { reject(new Error(resp?.error?.description || t("checkout.errFailed"))); });
      rzp.open();
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        items: items.map(it => ({ plan_id: it.plan_id, file_type: it.file_type })),
        billing_name: form.billing_name.trim(),
        billing_mobile: form.billing_mobile.trim(),
        billing_email: form.billing_email.trim() || null,
        billing_address: form.billing_address.trim(),
        billing_city: form.billing_city.trim(),
        billing_state: form.billing_state,
        billing_pincode: form.billing_pincode.trim(),
        is_business: form.is_business,
        business_name: form.is_business ? form.business_name.trim() : null,
        gstin: form.is_business ? form.gstin.toUpperCase().trim() : null,
        create_account: !user && form.create_account,
        password: !user && form.create_account ? form.password : null,
      };
      const { data: orderResp } = await api.post("/checkout/create", payload);
      if (orderResp.account_created) {
        // Pull the new auth cookie into AuthContext so the dashboard etc. work.
        try { await refreshAuth(); } catch {}
      }
      const rzpResp = await launchRazorpay(orderResp);
      const { data: verifyResp } = await api.post("/checkout/verify", {
        order_id: orderResp.order_id,
        razorpay_order_id: rzpResp.razorpay_order_id,
        razorpay_payment_id: rzpResp.razorpay_payment_id,
        razorpay_signature: rzpResp.razorpay_signature,
      });
      // Mark paid BEFORE clearing the cart so the empty-cart guard above
      // doesn't bounce the user to /cart while we navigate to success.
      setPaid(true);
      clear();
      nav(`/order/success?order_id=${verifyResp.order_id}&mobile=${encodeURIComponent(form.billing_mobile)}`);
    } catch (err) {
      const apiDetail = err.response?.data?.detail;
      setError(apiDetail ? formatApiErrorDetail(apiDetail) : (err.message || t("checkout.errFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  if (count === 0) return null;

  return (
    <div className="bg-slate-50/50 min-h-screen" data-testid="checkout-page">
      <div className="bg-white border-b border-slate-200">
        <div className="nc-container py-3">
          <Link to="/cart" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700">
            <ArrowLeft className="w-4 h-4" /> {t("checkout.backToCart")}
          </Link>
        </div>
      </div>

      <div className="nc-container py-8 grid lg:grid-cols-12 gap-6">
        <form onSubmit={submit} className="lg:col-span-7 space-y-4">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("checkout.title")}</h1>

          {/* Buyer type */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-semibold text-slate-900 text-sm uppercase tracking-wider mb-3">{t("checkout.buyerType")}</h2>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => update("is_business", false)}
                className={`p-3 rounded-xl border-2 text-sm font-medium flex items-center gap-2 justify-center transition ${!form.is_business ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid="buyer-individual-btn">
                <User className="w-4 h-4" /> {t("checkout.individual")}
              </button>
              <button type="button" onClick={() => update("is_business", true)}
                className={`p-3 rounded-xl border-2 text-sm font-medium flex items-center gap-2 justify-center transition ${form.is_business ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid="buyer-business-btn">
                <Building2 className="w-4 h-4" /> {t("checkout.business")}
              </button>
            </div>

            {form.is_business && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-1">
                  <label className="text-xs font-medium text-slate-600">{t("checkout.businessName")} *</label>
                  <input className="nc-input mt-1" value={form.business_name} onChange={e => update("business_name", e.target.value)} data-testid="business-name-input" />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs font-medium text-slate-600">{t("checkout.gstin")} *</label>
                  <input className="nc-input mt-1 uppercase" placeholder={t("checkout.gstinPh")} value={form.gstin}
                    onChange={e => update("gstin", e.target.value.toUpperCase())} maxLength={15} data-testid="gstin-input" />
                </div>
              </div>
            )}
          </div>

          {/* Billing */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-semibold text-slate-900 text-sm uppercase tracking-wider mb-3">{t("checkout.billingAddress")}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">{t("checkout.fullName")} *</label>
                <input className="nc-input mt-1" value={form.billing_name} onChange={e => update("billing_name", e.target.value)} data-testid="billing-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">{t("checkout.mobile")} *</label>
                <input className="nc-input mt-1" maxLength={10} value={form.billing_mobile} onChange={e => update("billing_mobile", e.target.value.replace(/\D/g, ""))} data-testid="billing-mobile" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">{t("checkout.email")}</label>
                <input type="email" className="nc-input mt-1" value={form.billing_email} onChange={e => update("billing_email", e.target.value)} data-testid="billing-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">{t("checkout.address")} *</label>
                <textarea className="nc-input mt-1" rows={2} value={form.billing_address} onChange={e => update("billing_address", e.target.value)} data-testid="billing-address" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">{t("checkout.city")} *</label>
                <input className="nc-input mt-1" value={form.billing_city} onChange={e => update("billing_city", e.target.value)} data-testid="billing-city" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">{t("checkout.state")} *</label>
                <select className="nc-input mt-1" value={form.billing_state} onChange={e => update("billing_state", e.target.value)} data-testid="billing-state">
                  <option value="">{t("checkout.selectState")}</option>
                  {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">{t("checkout.pincode")} *</label>
                <input className="nc-input mt-1" maxLength={6} value={form.billing_pincode} onChange={e => update("billing_pincode", e.target.value.replace(/\D/g, ""))} data-testid="billing-pincode" />
              </div>
            </div>
          </div>

          {/* Account creation for guests */}
          {!user && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.create_account}
                  onChange={e => update("create_account", e.target.checked)}
                  className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                  data-testid="create-account-toggle" />
                <span className="font-semibold text-slate-900 text-sm">Create an account</span>
                <span className="text-xs text-slate-500">— track your orders & re-download anytime.</span>
              </label>
              {form.create_account && (
                <div className="mt-3">
                  <label className="text-xs font-medium text-slate-600">Choose a password *</label>
                  <div className="relative mt-1">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type={showPassword ? "text" : "password"}
                      className="nc-input pl-9 pr-10"
                      value={form.password}
                      onChange={e => update("password", e.target.value)}
                      placeholder="At least 6 characters"
                      autoComplete="new-password"
                      data-testid="checkout-password" />
                    <button type="button" onClick={() => setShowPassword(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">
                    You'll log in with mobile <span className="font-medium">{form.billing_mobile || "(enter mobile above)"}</span> + this password.
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700" data-testid="checkout-error">{error}</div>
          )}

          <button type="submit" disabled={submitting} className="nc-btn-primary w-full justify-center text-base py-3" data-testid="pay-now-btn">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
            {submitting ? t("checkout.processing") : `${t("checkout.pay")} ${formatINR(total)}`}
          </button>
          <p className="text-xs text-slate-500 text-center">
            {t("checkout.paymentNote")}
          </p>
        </form>

        {/* Right column: order summary */}
        <div className="lg:col-span-5">
          <div className="sticky top-20 bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-semibold text-slate-900 text-base">{t("checkout.summary")}</h2>
            <div className="mt-4 divide-y divide-slate-100">
              {items.map((it, i) => {
                const Icon = fileTypeIcon(it.file_type);
                return (
                  <div key={`${it.plan_id}-${it.file_type}`} className="py-3 flex items-start gap-3" data-testid={`summary-item-${i}`}>
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{it.plan_title}</p>
                      <p className="text-xs text-slate-500">{t(fileTypeLabelKey(it.file_type))}</p>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 whitespace-nowrap">{formatINR(it.price_paise)}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 border-t border-slate-200 pt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t("cart.subtotal")}</span>
                <span className="font-medium text-slate-900">{formatINR(subtotalPaise)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t("cart.gst")} ({GST_PERCENT}%)</span>
                <span className="font-medium text-slate-900">{formatINR(gst)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-2">
                <span className="font-semibold text-slate-900">{t("checkout.totalPayable")}</span>
                <span className="text-xl font-bold text-slate-900" data-testid="checkout-total">{formatINR(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
